import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Create axios instance with default config
const audioApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 2 minutes for audio processing
});

// Helper function to get current project ID
const getCurrentProjectId = (): string | null => {
  return localStorage.getItem('selectedProjectId');
};

// Helper function to add project ID to request data
const addProjectToData = (data: any): any => {
  const projectId = getCurrentProjectId();
  if (projectId) {
    if (data instanceof FormData) {
      data.append('projectId', projectId);
      return data;
    } else {
      return { ...data, projectId };
    }
  }
  return data;
};

// Add auth token and project context to requests
audioApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add project ID to requests for transactional APIs (exclude voices which are org-wide)
  const projectId = getCurrentProjectId();
  const url = config.url || '';

  const isVoiceEndpoint = url.startsWith('/voices') || url.includes('/voices/');

  if (projectId && !isVoiceEndpoint) {
    // Send project context as header
    config.headers['X-Project-Id'] = projectId;

    // For GET requests, also add as query param for convenience/filters
    if (config.method === 'get') {
      config.params = { ...config.params, projectId };
    }
  }
  
  return config;
});

// Response interceptor for error handling
audioApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Audio API Error:', error.response?.data || error.message);
    throw error;
  }
);

// Voice Management API
export const voiceApi = {
  // Get all voices
  getVoices: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    language?: string;
  }) => {
    const response = await audioApi.get('/voices', { params });
    return response.data;
  },

  // Get voice by ID
  getVoice: async (id: string) => {
    const response = await audioApi.get(`/voices/${id}`);
    return response.data;
  },

  // Create new voice
  createVoice: async (formData: FormData) => {
    const response = await audioApi.post('/voices', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Update voice
  updateVoice: async (id: string, data: FormData | any) => {
    const config = data instanceof FormData ? {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    } : {};
    
    const response = await audioApi.put(`/voices/${id}`, data, config);
    return response.data;
  },

  // Delete voice
  deleteVoice: async (id: string) => {
    const response = await audioApi.delete(`/voices/${id}`);
    return response.data;
  },

  // Add samples to voice
  addSamples: async (id: string, formData: FormData) => {
    const response = await audioApi.post(`/voices/${id}/samples`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get voice statistics
  getVoiceStats: async () => {
    const response = await audioApi.get('/voices/stats/overview');
    return response.data;
  },

  // Sync ElevenLabs voice metadata into backend
  syncElevenLabs: async () => {
    const response = await audioApi.post('/voices/sync-elevenlabs');
    return response.data;
  },

  // List ElevenLabs voices not yet imported into this org
  getElevenLabsAvailable: async () => {
    const response = await audioApi.get('/voices/elevenlabs-available');
    return response.data;
  },

  // Import selected ElevenLabs voices by their ElevenLabs voice IDs
  importElevenLabsVoices: async (voiceIds: string[]) => {
    const response = await audioApi.post('/voices/import-elevenlabs', { voiceIds });
    return response.data;
  },

  // List Sarvam TTS speakers not yet imported into this org
  getSarvamAvailable: async () => {
    const response = await audioApi.get('/voices/sarvam-available');
    return response.data;
  },

  // Import selected Sarvam speakers by their speaker IDs
  importSarvamVoices: async (speakerIds: string[]) => {
    const response = await audioApi.post('/voices/import-sarvam', { speakerIds });
    return response.data;
  },

  // Generate a short spoken sample for a Sarvam speaker before importing
  previewSarvamVoice: async (speaker: string, language?: string) => {
    const response = await audioApi.post('/voices/sarvam-preview', { speaker, language });
    return response.data;
  },
};

// Audio Translation API
export const translationApi = {
  // Start audio translation
  startTranslation: async (formData: FormData, onUploadProgress?: (percent: number) => void) => {
    const response = await audioApi.post('/audio/translate', formData, {
      onUploadProgress: onUploadProgress
        ? (event) => {
            if (event.total) onUploadProgress(Math.round((event.loaded * 100) / event.total));
          }
        : undefined,
    });
    return response.data;
  },

  // Get translation job status
  getTranslationStatus: async (jobId: string) => {
    const response = await audioApi.get(`/audio/translate/${jobId}`);
    return response.data;
  },

  // Get translation jobs
  getTranslationJobs: async (params?: {
    status?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await audioApi.get('/audio/jobs', { params });
    return response.data;
  },

  // Cancel translation job
  cancelTranslation: async (jobId: string) => {
    const response = await audioApi.delete(`/audio/jobs/${jobId}`);
    return response.data;
  },

  // Download a protected audio file using auth headers
  downloadFile: async (apiUrl: string, suggestedFilename: string): Promise<void> => {
    const path = apiUrl.replace(/^https?:\/\/[^/]+/, '').replace(/^\/api/, '');
    const response = await audioApi.get(path, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = suggestedFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },

  // Get translation statistics
  getTranslationStats: async () => {
    const response = await audioApi.get('/audio/stats');
    return response.data;
  },

  // Get detailed job information
  getJobDetails: async (jobId: string) => {
    const response = await audioApi.get(`/audio/jobs/${jobId}/details`);
    return response.data;
  },

  // Update step data (for editing transcribed/translated text)
  updateStepData: async (jobId: string, stepId: string, data: any) => {
    const response = await audioApi.put(`/audio/jobs/${jobId}/steps/${stepId}`, data);
    return response.data;
  },

  // Execute / recompute a step (optionally with provider overrides)
  executeStep: async (jobId: string, stepId: string, data?: StepExecutionOverrides) => {
    const response = await audioApi.post(`/audio/jobs/${jobId}/steps/${stepId}/execute`, data);
    return response.data;
  },

  // Continue pipeline from a step after editing
  continueStep: async (jobId: string, stepId: string, data?: { transcribedText?: string; translatedText?: string }) => {
    const response = await audioApi.post(`/audio/jobs/${jobId}/steps/${stepId}/continue`, data);
    return response.data;
  },

  // Retry step from a specific point
  retryFromStep: async (jobId: string, stepId: string) => {
    const response = await audioApi.post(`/audio/jobs/${jobId}/retry-from/${stepId}`);
    return response.data;
  },

  // Job-level retry — auto-detects the first non-completed step
  retryJob: async (jobId: string) => {
    const response = await audioApi.post(`/audio/jobs/${jobId}/retry`);
    return response.data;
  },
};

// Speech-to-Text API
export const speechToTextApi = {
  // Convert speech to text
  transcribeAudio: async (formData: FormData) => {
    const response = await audioApi.post('/audio/speech-to-text', formData);
    return response.data;
  },

  // Get STT job history
  getJobs: async (params?: { status?: string; page?: number; limit?: number }) => {
    const response = await audioApi.get('/audio/stt-jobs', { params });
    return response.data;
  },

  // Get STT job status (for polling until completed)
  getSttJob: async (jobId: string) => {
    const response = await audioApi.get(`/audio/speech-to-text/${jobId}`);
    return response.data;
  },

  // Retry a failed job — re-runs with the job's own stored request
  retryJob: async (jobId: string) => {
    const response = await audioApi.post(`/audio/speech-to-text/${jobId}/retry`);
    return response.data;
  },
};

// Text-to-Speech API
export const textToSpeechApi = {
  // Convert text to speech
  generateSpeech: async (data: {
    text: string;
    voiceId: string;
    title?: string;
    provider?: 'elevenlabs' | 'sarvam' | 'ai4bharat';
    language?: string;
    modelId?: string;
    outputFormat?: string;
    settings?: {
      speed?: number;
      stability?: number;
      similarityBoost?: number;
    };
  }) => {
    const response = await audioApi.post('/audio/text-to-speech', data);
    return response.data;
  },

  // Get TTS job status
  getTtsJob: async (jobId: string) => {
    const response = await audioApi.get(`/audio/text-to-speech/${jobId}`);
    return response.data;
  },

  // Public (unauthenticated, unguessable-UUID) URL that serves the generated
  // audio directly — safe to use as an <audio src> or download link.
  getTtsDownloadUrl: (jobId: string) => {
    return `${API_BASE_URL}/audio/text-to-speech/${jobId}/download`;
  },

  // Get TTS job history
  getJobs: async (params?: { status?: string; page?: number; limit?: number }) => {
    const response = await audioApi.get('/audio/tts-jobs', { params });
    return response.data;
  },

  // Retry a failed job — re-runs with the job's own stored request, unless
  // `text` is provided to edit it before retrying
  retryJob: async (jobId: string, overrides?: { text?: string }) => {
    const response = await audioApi.post(`/audio/text-to-speech/${jobId}/retry`, overrides || {});
    return response.data;
  },
};

// Utility APIs
export const audioUtilsApi = {
  // Get supported languages
  getSupportedLanguages: async () => {
    const response = await audioApi.get('/audio/languages');
    return response.data;
  },

  // Get available voices
  getAvailableVoices: async () => {
    const response = await audioApi.get('/audio/voices');
    return response.data;
  },

  // Get service health
  getServiceHealth: async () => {
    const response = await audioApi.get('/audio/health');
    return response.data;
  },

  // Get audio file URL
  getAudioFileUrl: (filename: string) => {
    return `${API_BASE_URL}/audio/files/${filename}`;
  },

  // Get translation file URL
  getTranslationFileUrl: (jobId: string, filename: string) => {
    return `${API_BASE_URL}/audio/translations/${jobId}/${filename}`;
  },
};

export type VoiceProvider = 'elevenlabs' | 'sarvam' | 'ai4bharat';
export type AudioSTTProvider = 'sarvam' | 'ai4bharat';
export type AudioTranslationProvider = 'sarvam' | 'google' | 'deepl';

export interface StepExecutionOverrides {
  sttProvider?: AudioSTTProvider;
  translationProvider?: AudioTranslationProvider;
  ttsProvider?: VoiceProvider;
  voiceId?: string;
  runAlone?: boolean;
  transcribedText?: string;
  translatedText?: string;
}

// Types for TypeScript
export interface Voice {
    _id: string;
    name: string;
    description?: string;
    organizationId: string;
    createdBy: string;
    provider: VoiceProvider;
    elevenLabsVoiceId?: string;
    ai4bharatReferenceAudioPath?: string;
    ai4bharatReferenceText?: string;
    sarvamSpeakerId?: string;
    sarvamModel?: string;
    gender?: 'male' | 'female' | 'neutral';
    age?: 'child' | 'young_adult' | 'adult' | 'senior';
    accent?: string;
    language: string;
    sampleFiles: AudioSample[];
    settings: VoiceSettings;
    status: 'training' | 'ready' | 'failed' | 'deleted';
    trainingProgress?: number;
    trainingStartedAt?: string;
    trainingCompletedAt?: string;
    errorMessage?: string;
    usageCount: number;
    lastUsedAt?: string;
    isPublic: boolean;
    isActive: boolean;
    isCustom: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface AudioSample {
    _id: string;
    filename: string;
    originalName: string;
    path: string;
    url: string;
    size: number;
    duration?: number;
    uploadedAt: string;
}

export interface VoiceSettings {
    stability: number;
    similarityBoost: number;
    style: number;
    speakerBoost: boolean;
}

export interface AudioTranslationRequestInfo {
  audioFile?: string;
  sourceLanguage?: string;
  targetLanguage: string;
  voiceId: string;
  sttProvider?: AudioSTTProvider;
  translationProvider?: AudioTranslationProvider;
  ttsProvider?: VoiceProvider;
  outputFormat?: 'mp3' | 'wav' | 'ogg';
  enhanceAudio?: boolean;
  preserveEmotion?: boolean;
  customPrompt?: string;
}

export interface TranslationJob {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  currentStep: string;
  steps: TranslationStep[];
  request?: AudioTranslationRequestInfo;
  result?: TranslationResult;
  error?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TranslationStep {
  id: string;
  name: string;
  description: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped';
  order: number;
  startedAt?: string;
  completedAt?: string;
  updatedAt?: string;
  duration?: number;
  data?: {
    // Step 1: Audio Upload
    originalAudioUrl?: string;
    originalAudioSize?: number;
    originalAudioFormat?: string;
    originalAudioDuration?: number;
    
    // Step 2: Speech-to-Text
    transcribedText?: string;
    transcriptionLanguage?: string;
    transcriptionConfidence?: number;
    
    // Step 3: Text Translation
    translatedText?: string;
    sourceLanguage?: string;
    targetLanguage?: string;
    translationConfidence?: number;
    
    // Step 4: Text-to-Speech
    voiceId?: string;
    voiceName?: string;
    outputAudioUrl?: string;
    outputAudioSize?: number;
    outputAudioFormat?: string;
    outputAudioDuration?: number;
  };
  error?: string;
  canEdit?: boolean;
  canRerun?: boolean;
}

export interface TranslationResult {
  success: boolean;
  translationId: string;
  originalTranscript: string;
  translatedText: string;
  sourceLanguage: string;
  targetLanguage: string;
  voiceName: string;
  outputAudioPath: string;
  outputAudioUrl: string;
  duration: {
    original: number;
    translated: number;
  };
  confidence: {
    transcription: number;
    translation: number;
  };
  processingTime: number;
  metadata: {
    originalFileSize: number;
    outputFileSize: number;
    audioFormat: string;
    sampleRate?: number;
  };
}

export interface Language {
  code: string;
  name: string;
  nativeName: string;
  supported: boolean;
}

export interface TranscriptionResult {
  transcript: string;
  language: string;
  confidence: number;
  duration: number;
  segments?: Array<{
    start: number;
    end: number;
    text: string;
    confidence: number;
    speaker?: string;
  }>;
  words?: Array<{
    word: string;
    start: number;
    end: number;
    confidence: number;
  }>;
}

export interface TTSResult {
  audioUrl: string;
  fileName: string;
  size: number;
  format: string;
  voiceName: string;
  textLength: number;
}

export interface TextToSpeechRequestInfo {
  text: string;
  voiceId: string;
  provider?: VoiceProvider;
  language?: string;
  referenceAudioPath?: string;
  referenceText?: string;
  modelId?: string;
  outputFormat?: 'mp3' | 'wav' | 'ogg';
  voiceSettings?: {
    stability?: number;
    similarityBoost?: number;
    style?: number;
    useSpeakerBoost?: boolean;
    speed?: number;
  };
}

export interface TextToSpeechResultInfo {
  audioUrl: string;
  audioPath: string;
  fileName: string;
  size: number;
  duration: number;
  format: string;
  processingTime: number;
}

export interface TextToSpeechJob {
  jobId: string;
  title?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  currentStep: string;
  request?: TextToSpeechRequestInfo;
  result?: TextToSpeechResultInfo;
  error?: string;
  createdAt: string;
  updatedAt: string;
}

export default audioApi;
